import { useState } from 'react';
import { Consents, ConsentManagementSettingsData, ConsentsCheckboxSettingsData } from '../../models';
import { useTranslations } from '../../store/translations/hooks';
import { TK } from '../../store/translations/translationKeys';
import { getConsentLabel as getConsentLabelUtil } from '../../shared/consentUtils';

const hasAnyConsentEnabled = (consents: Consents): boolean =>
    Object.values(consents).some(Boolean);

export const useConsentSelection = () => {
    const t = useTranslations();

    const [selectedConsents, setSelectedConsents] = useState<Consents>({
        gdpr: false,
        healthDataProcessing: false,
        emailMarketing: false,
        smsMarketing: false,
        birthdayMessage: false,
        emailReminders: false,
        smsReminders: false,
        whatsAppReminders: false,
        dataSharingWithProfessionals: false,
        thirdPartyDataSharing: false,
        imageOrVideoCaptureForInstitutionalPurposes: false
    });

    // Tracks approved state for checkboxes with no associated enum consents, keyed by checkbox ID.
    const [customCheckboxStates, setCustomCheckboxStates] = useState<Record<string, boolean>>({});

    const handleConsentToggle = (consentKey: keyof Consents, checked: boolean) => {
        setSelectedConsents(prev => ({ ...prev, [consentKey]: checked }));
    };

    const handleCheckboxToggle = (checkbox: ConsentsCheckboxSettingsData, checked: boolean) => {
        if (hasAnyConsentEnabled(checkbox.consents)) {
            const updated = { ...selectedConsents };
            (Object.keys(checkbox.consents) as Array<keyof Consents>).forEach(key => {
                if (checkbox.consents[key]) updated[key] = checked;
            });
            setSelectedConsents(updated);
        } else if (checkbox.text) {
            setCustomCheckboxStates(prev => ({ ...prev, [checkbox.text]: checked }));
        }
    };

    const isCheckboxSelected = (checkbox: ConsentsCheckboxSettingsData): boolean => {
        if (hasAnyConsentEnabled(checkbox.consents)) {
            return (Object.keys(checkbox.consents) as Array<keyof Consents>).every(
                key => !checkbox.consents[key] || selectedConsents[key]
            );
        }
        return customCheckboxStates[checkbox.text] ?? false;
    };

    const getApprovedCustomCheckboxTexts = (): string[] =>
        Object.entries(customCheckboxStates)
            .filter(([, approved]) => approved)
            .map(([text]) => text);

    const getConsentLabel = (consentKey: keyof Consents): string =>
        getConsentLabelUtil(consentKey, t);

    const validateMandatoryConsents = (consentData: ConsentManagementSettingsData): string | null => {
        const missingMandatory = consentData.consentsCheckboxes
            .filter(c => c.isMandatory)
            .some(c => !isCheckboxSelected(c));

        return missingMandatory ? t(TK.consentMandatoryConsentsRequired) : null;
    };

    return {
        selectedConsents,
        customCheckboxStates,
        handleConsentToggle,
        handleCheckboxToggle,
        isCheckboxSelected,
        getApprovedCustomCheckboxTexts,
        getConsentLabel,
        validateMandatoryConsents
    };
};
